// cookieManager.js
// Modul für Cookie-Management und iframe-Handling

const COOKIE_BASE = 'cookie_basefeatures';
const COOKIE_IFRAMES = 'cookie_iframes';
const SCRIPT_SOURCE_ATTR = 'data-external-source';
const SCRIPT_SCOPE_ATTR = 'data-consent-scope';
const SCRIPT_LOADED_ATTR = 'data-external-loaded';
const SCRIPT_LOADING_ATTR = 'data-external-loading';
const FRAME_SOURCE_ATTR = 'data-source';
const DEFAULT_SCRIPT_SCOPE = 'base';

let frameObserver = null;
let scriptObserver = null;
let scriptObserverActive = false;
let injectedScriptSourcesHydrated = false;
let cookiebarSaveListenerRegistered = false;
const injectedScriptSources = new Set();
const pendingExternalScriptLoads = new Map();
const CONSENT_COOKIE_MAP = {
    base: COOKIE_BASE,
    iframe: COOKIE_IFRAMES,
};

const resolveCspNonce = () => {
    const windowNonce = typeof window !== 'undefined' && typeof window.__cspNonce === 'string'
        ? window.__cspNonce.trim()
        : '';
    if (windowNonce) {
        return windowNonce;
    }

    const scriptWithNonce = document.querySelector('script[nonce]');
    const scriptNonce = scriptWithNonce?.getAttribute('nonce')?.trim() || '';
    if (scriptNonce) {
        return scriptNonce;
    }

    return document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')?.trim() || '';
};

const applyCspNonceToScript = (script) => {
    if (!script || typeof script.setAttribute !== 'function') {
        return;
    }

    if (script.getAttribute('nonce')) {
        return;
    }

    const nonce = resolveCspNonce();
    if (nonce) {
        script.setAttribute('nonce', nonce);
    }
};

const hasConsent = (cookieName) => {
    if (!cookieName) {
        return false;
    }
    return document.cookie.split(';').some(cookie => cookie.trim().startsWith(`${cookieName}=`));
};

/**
 * Initialisiert Funktionen nach Cookie-Zustand.
 */
export function setupFunctions() {
    const baseConsent = hasConsent(COOKIE_BASE);
    const frameConsent = hasConsent(COOKIE_IFRAMES);

    initFrames(frameConsent);
    initBaseFeatures(baseConsent);
    initConsentAwareScripts();

    if (!cookiebarSaveListenerRegistered) {
        cookiebarSaveListenerRegistered = true;
        window.addEventListener('cookiebar_save', () => {
            setTimeout(() => {
                const newFrameConsent = hasConsent(COOKIE_IFRAMES);
                const newBaseConsent = hasConsent(COOKIE_BASE);

                initFrames(newFrameConsent);
                initBaseFeatures(newBaseConsent);
                initConsentAwareScripts();
            }, 150);
        });
    }
}

/**
 * Initialisiert externe Scripts basierend auf Cookie-Zustand.
 */
function initConsentAwareScripts(root = document) {
    ensureInjectedSourcesHydrated();
    ensureScriptObserver();
    const placeholders = collectScriptPlaceholders(root);
    if (placeholders.length === 0) {
        return;
    }

    const injectScripts = () => {
        placeholders
            .filter(placeholder => !isPlaceholderLoadingLocked(placeholder))
            .forEach(loadExternalScript);
    };

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(injectScripts, { timeout: 500 });
    } else {
        setTimeout(injectScripts, 0);
    }
}

/**
 * Initialisiert iframes basierend auf dem Cookie-Zustand.
 */
const activateDeferredSource = (element) => {
    if (!element) {
        return;
    }

    const source = element.getAttribute(FRAME_SOURCE_ATTR);
    if (!source) {
        return;
    }

    const tagName = element.tagName.toLowerCase();

    if (tagName === 'script') {
        if (element.dataset.lazyLoading === '1' || element.dataset.lazyLoaded === '1') {
            return;
        }

        const script = document.createElement('script');
        element.dataset.lazyLoading = '1';
        applyCspNonceToScript(script);

        Array.from(element.attributes).forEach((attr) => {
            if (attr.name !== FRAME_SOURCE_ATTR && attr.name !== 'data-lazy-loading' && attr.name !== 'data-lazy-loaded') {
                script.setAttribute(attr.name, attr.value);
            }
        });

        script.src = source;
        script.addEventListener('load', () => {
            element.dataset.lazyLoaded = '1';
            delete element.dataset.lazyLoading;
        }, { once: true });
        script.addEventListener('error', () => {
            delete element.dataset.lazyLoading;
            delete element.dataset.lazyLoaded;
            script.remove();
            console.warn('[consent] deferred script failed to load', source);
        }, { once: true });

        document.head.appendChild(script);
        return;
    }

    element.src = source;
    element.dataset.lazyLoaded = '1';
};

const ensureFrameObserver = () => {
    if (frameObserver || typeof IntersectionObserver === 'undefined') {
        return frameObserver;
    }

    frameObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            const target = entry.target;
            activateDeferredSource(target);
            observer.unobserve(target);
        });
    }, {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0,
    });

    return frameObserver;
};

const isPlaceholderLoadingLocked = (placeholder) => {
    const source = placeholder.getAttribute(SCRIPT_SOURCE_ATTR);
    if (!source) {
        return true;
    }

    if (placeholder.getAttribute(SCRIPT_LOADED_ATTR) === '1') {
        return true;
    }

    if (placeholder.getAttribute(SCRIPT_LOADING_ATTR) === '1') {
        return true;
    }

    const scopes = resolveConsentScopes(placeholder);
    return scopes.length > 0 && !hasConsentForScopes(scopes);
};

const collectScriptPlaceholders = (root = document) => {
    if (!root) {
        return [];
    }

    const matchesRoot = root.matches?.(`script[${SCRIPT_SOURCE_ATTR}]`);
    const candidates = matchesRoot
        ? [root]
        : Array.from(root.querySelectorAll?.(`script[${SCRIPT_SOURCE_ATTR}]`) || []);

    return candidates.filter((candidate) => candidate.getAttribute(SCRIPT_LOADED_ATTR) !== '1');
};

const loadExternalScript = (placeholder) => {
    if (!placeholder || placeholder.getAttribute(SCRIPT_LOADED_ATTR) === '1' || placeholder.getAttribute(SCRIPT_LOADING_ATTR) === '1') {
        return;
    }

    const source = placeholder.getAttribute(SCRIPT_SOURCE_ATTR);
    if (!source) {
        placeholder.setAttribute(SCRIPT_LOADED_ATTR, '1');
        placeholder.remove();
        return;
    }

    const consentScopes = resolveConsentScopes(placeholder);
    if (!hasConsentForScopes(consentScopes)) {
        return;
    }

    const resolvedSource = resolveSourceUrl(source);
    if (isSourceAlreadyInjected(resolvedSource)) {
        markPlaceholderLoaded(placeholder);
        return;
    }

    const pendingLoad = pendingExternalScriptLoads.get(resolvedSource);
    if (pendingLoad) {
        placeholder.setAttribute(SCRIPT_LOADING_ATTR, '1');
        pendingLoad
            .then(() => markPlaceholderLoaded(placeholder))
            .catch(() => {
                if (placeholder.isConnected) {
                    placeholder.removeAttribute(SCRIPT_LOADING_ATTR);
                }
            });
        return;
    }

    const script = document.createElement('script');
    script.src = source;
    applyCspNonceToScript(script);

    Array.from(placeholder.attributes).forEach((attr) => {
        if (attr.name === SCRIPT_SOURCE_ATTR || attr.name === SCRIPT_LOADED_ATTR || attr.name === SCRIPT_LOADING_ATTR) {
            return;
        }
        script.setAttribute(attr.name, attr.value);
    });

    placeholder.setAttribute(SCRIPT_LOADING_ATTR, '1');

    const loadPromise = new Promise((resolve, reject) => {
        script.addEventListener('load', () => {
            rememberInjectedSource(resolvedSource);
            resolve();
        }, { once: true });
        script.addEventListener('error', () => {
            script.remove();
            reject(new Error(`[consent] external script failed to load: ${source}`));
        }, { once: true });
    });

    pendingExternalScriptLoads.set(resolvedSource, loadPromise);
    loadPromise
        .then(() => {
            markPlaceholderLoaded(placeholder);
        })
        .catch((error) => {
            if (placeholder.isConnected) {
                placeholder.removeAttribute(SCRIPT_LOADING_ATTR);
            }
            console.warn(error.message);
        })
        .finally(() => {
            pendingExternalScriptLoads.delete(resolvedSource);
        });

    document.head.appendChild(script);
};

const markPlaceholderLoaded = (placeholder) => {
    if (!placeholder) {
        return;
    }

    placeholder.removeAttribute(SCRIPT_LOADING_ATTR);
    placeholder.setAttribute(SCRIPT_LOADED_ATTR, '1');
    placeholder.remove();
};

const resolveConsentScopes = (element) => {
    if (!element) {
        return [DEFAULT_SCRIPT_SCOPE];
    }

    const raw = element.getAttribute(SCRIPT_SCOPE_ATTR);
    if (!raw) {
        return [DEFAULT_SCRIPT_SCOPE];
    }

    return raw
        .split(/[\s,]+/)
        .map(scope => scope.trim())
        .filter(Boolean);
};

const hasConsentForScopes = (scopes) => {
    if (!scopes || scopes.length === 0) {
        return true;
    }

    return scopes.every((scope) => {
        const cookieName = CONSENT_COOKIE_MAP[scope] || scope;
        return hasConsent(cookieName);
    });
};

const resolveSourceUrl = (source) => {
    if (!source) {
        return '';
    }

    try {
        return new URL(source, document.baseURI).href;
    } catch (error) {
        return source;
    }
};

const rememberInjectedSource = (source) => {
    if (!source) {
        return;
    }
    injectedScriptSources.add(source);
};

const isSourceAlreadyInjected = (source) => {
    if (!source) {
        return false;
    }

    if (injectedScriptSources.has(source)) {
        return true;
    }

    if (!injectedScriptSourcesHydrated) {
        ensureInjectedSourcesHydrated();
        return injectedScriptSources.has(source);
    }

    return false;
};

const ensureInjectedSourcesHydrated = () => {
    if (injectedScriptSourcesHydrated) {
        return;
    }

    document.querySelectorAll('script[src]').forEach((script) => {
        const src = script.getAttribute('src');
        const resolved = resolveSourceUrl(src);
        if (resolved) {
            injectedScriptSources.add(resolved);
        }
    });

    injectedScriptSourcesHydrated = true;
};

const ensureScriptObserver = () => {
    if (scriptObserverActive || typeof MutationObserver === 'undefined' || !document.body) {
        return;
    }

    if (!scriptObserver) {
        scriptObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }
                    initConsentAwareScripts(node);
                });
            });
        });
    }

    scriptObserver.observe(document.body, {
        childList: true,
        subtree: true,
    });
    scriptObserverActive = true;
};

function initFrames(allowFrames) {
    const candidates = document.querySelectorAll(`[${FRAME_SOURCE_ATTR}]`);
    if (!candidates.length) {
        return;
    }

    if (!allowFrames) {
        const observer = ensureFrameObserver();
        candidates.forEach((element) => {
            observer?.unobserve(element);

            if (element.tagName.toLowerCase() !== 'script') {
                element.removeAttribute('src');
                element.src = 'iframe.php';
            }
            delete element.dataset.lazyLoading;
            delete element.dataset.lazyLoaded;
        });
        return;
    }

    const observer = ensureFrameObserver();

    candidates.forEach((element) => {
        if (element.dataset.lazyLoaded === '1') {
            return;
        }

        if (!observer) {
            activateDeferredSource(element);
            return;
        }

        observer.observe(element);
    });
}

function initBaseFeatures(consentGranted) {
    if (!consentGranted) {
        return;
    }
    // Basisfunktionen hier
}

function getLanguage() {
    return document.documentElement.lang || 'de';
}

export function resetCookies() {
    const language = getLanguage();
    let message = "This will delete all cookies and reload the page, continue?";

    if (language === 'de') {
        message = "Dadurch werden alle Cookies gelöscht und die Seite wird neu geladen, fortfahren?";
    } else if (language === 'en') {
        message = "This will delete all cookies and reload the page, continue?";
    }

    if (confirm(message)) {
        window.localStorage.clear();
        const cookies = document.cookie.split(";");
        cookies.forEach(cookie => {
            document.cookie = cookie.trim().split('=')[0] + "=;expires=" + new Date().toUTCString() + ";path=/";
        });
        window.location.reload();
    }
}
